import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarOff, Plus, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useServices } from "@/lib/useServices";
import { Empty, Loading, PageHead, fmtWhen, useAdmin } from "./ui";

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const COLORS = ["#c9a14a", "#d97b93", "#5b8def", "#3fa37a", "#9b6bd6", "#e08a3c", "#2bb3b1", "#7a7a7a"];
const blankHours = () => Object.fromEntries(DAYS.map(([d]) => [d, { on: d !== "sun", start: "10:00", end: "18:00" }]));
const BLANK = { name: "", title: "", color: COLORS[0], active: true, categories: [], services: [], hours: blankHours(), days_off: [], login_email: "", login_password: "" };
const toggle = (arr, v, on) => (on ? [...new Set([...arr, v])] : arr.filter((x) => x !== v));
const fmt12 = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`; };

const Specialties = ({ f, setF }) => {
  const { data } = useServices();
  if (!data) return <Loading />;
  return (
    <div className="tm-specs" data-testid="team-specialties">
      {data.categories.map((c) => {
        const all = f.categories.includes(c.id);
        return (
          <details key={c.id} className="tm-cat">
            <summary>
              <label className="st-check" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={all} onChange={(e) => setF({ ...f, categories: toggle(f.categories, c.id, e.target.checked) })} data-testid={`team-cat-${c.id}`} />
                <strong>All {c.label}</strong>
              </label>
              <small>{all ? "every service" : `${c.items.filter((i) => f.services.includes(i.key)).length} picked`}</small>
            </summary>
            <div className="st-grid">
              {c.items.map((i) => (
                <label key={i.key} className="st-check">
                  <input type="checkbox" disabled={all} checked={all || f.services.includes(i.key)} onChange={(e) => setF({ ...f, services: toggle(f.services, i.key, e.target.checked) })} data-testid={`team-svc-${i.key}`} /> {i.name}
                </label>
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
};

const Hours = ({ f, setF }) => {
  const set = (d, k, v) => setF({ ...f, hours: { ...f.hours, [d]: { ...f.hours[d], [k]: v } } });
  return (
    <div className="tm-hours" data-testid="team-hours">
      {DAYS.map(([d, l]) => {
        const h = f.hours[d];
        return (
          <div className="tm-hour" key={d}>
            <label className="st-check"><input type="checkbox" checked={h.on} onChange={(e) => set(d, "on", e.target.checked)} data-testid={`team-day-${d}`} /> {l}</label>
            {h.on ? (
              <span className="tm-times">
                <input type="time" step={3600} value={h.start} onChange={(e) => set(d, "start", e.target.value)} aria-label={`${l} start`} data-testid={`team-start-${d}`} />–
                <input type="time" step={3600} value={h.end} onChange={(e) => set(d, "end", e.target.value)} aria-label={`${l} finish`} data-testid={`team-end-${d}`} />
              </span>
            ) : <small className="muted-sm">Off</small>}
          </div>
        );
      })}
    </div>
  );
};

const DaysOff = ({ f, setF }) => {
  const [o, setO] = useState({ start: "", end: "", note: "" });
  const add = () => { if (!o.start) return; setF({ ...f, days_off: [...f.days_off, o] }); setO({ start: "", end: "", note: "" }); };
  return (
    <div data-testid="team-days-off">
      {f.days_off.map((d, i) => (
        <div className="ad-row" key={i} data-testid={`team-day-off-${i}`}>
          <span><CalendarOff size={14} /> {d.start}{d.end && d.end !== d.start ? ` → ${d.end}` : ""} {d.note && <small className="muted-sm">· {d.note}</small>}</span>
          <button type="button" className="ad-icon-btn" onClick={() => setF({ ...f, days_off: f.days_off.filter((_, j) => j !== i) })} aria-label="Remove day off" data-testid={`team-day-off-remove-${i}`}><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="tm-off-add">
        <input type="date" value={o.start} onChange={(e) => setO({ ...o, start: e.target.value })} aria-label="Off from" data-testid="team-off-start" />
        <input type="date" value={o.end} min={o.start} onChange={(e) => setO({ ...o, end: e.target.value })} aria-label="Off until (optional)" data-testid="team-off-end" />
        <input placeholder="Note (e.g. Vacation)" maxLength={80} value={o.note} onChange={(e) => setO({ ...o, note: e.target.value })} data-testid="team-off-note" />
        <button type="button" className="btn light" onClick={add} disabled={!o.start} data-testid="team-off-add-button"><Plus size={14} /> Add</button>
      </div>
    </div>
  );
};

const Editor = ({ m, onDone }) => {
  const [f, setF] = useState(m.id ? { ...BLANK, ...m, login_email: "", login_password: "" } : BLANK);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, login_email: f.login_email || null };
    try {
      if (m.id) await api.put(`/admin/team/${m.id}`, body); else await api.post("/admin/team", body);
      qc.invalidateQueries({ queryKey: ["admin"] });
      qc.invalidateQueries({ queryKey: ["team-staff"] });
      toast.success(m.id ? "Team member saved" : `${f.name} added`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form className="st-form" onSubmit={save} data-testid="team-form">
      <DialogTitle className="ad-msg-title">{m.id ? `Edit ${m.name}` : "Add team member"}</DialogTitle>
      <div className="contact-row">
        <label className="mu-field">Name<input required maxLength={60} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-name-input" /></label>
        <label className="mu-field">Title (optional)<input maxLength={60} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Sugaring specialist" data-testid="team-title-input" /></label>
      </div>
      <div className="mu-field">Calendar colour
        <div className="tm-colors">{COLORS.map((c) => <button type="button" key={c} className="tm-swatch" style={{ background: c }} aria-pressed={f.color === c} aria-label={`Colour ${c}`} onClick={() => setF({ ...f, color: c })} data-testid={`team-color-${c.slice(1)}`} />)}</div>
      </div>
      <label className="st-check"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="team-active-checkbox" /> Taking bookings</label>
      <h4 className="tm-h">Services they do</h4>
      <Specialties f={f} setF={setF} />
      <h4 className="tm-h">Weekly hours</h4>
      <Hours f={f} setF={setF} />
      <h4 className="tm-h">Days off &amp; vacation</h4>
      <DaysOff f={f} setF={setF} />
      <h4 className="tm-h">Login</h4>
      {m.login ? <p className="muted-sm" data-testid="team-login-info">Logs in as <strong>{m.login.email}</strong>. Reset the password or add tabs in Staff logins.</p> : (
        <div className="contact-row">
          <input type="email" placeholder="Login email (optional)" value={f.login_email} onChange={(e) => setF({ ...f, login_email: e.target.value })} data-testid="team-login-email-input" />
          <input type="text" placeholder="Password (8+ characters)" minLength={8} autoComplete="new-password" value={f.login_password} onChange={(e) => setF({ ...f, login_password: e.target.value })} data-testid="team-login-password-input" />
        </div>
      )}
      <button className="btn gold" type="submit" disabled={busy} data-testid="team-save-button">{busy ? "Saving…" : "Save"}</button>
    </form>
  );
};

const Card = ({ m, onEdit }) => {
  const qc = useQueryClient();
  const remove = async () => {
    if (!window.confirm(`Remove ${m.name}? Their future bookings become Unassigned and their login is deleted.`)) return;
    try { const { data } = await api.delete(`/admin/team/${m.id}`); qc.invalidateQueries({ queryKey: ["admin"] }); toast.success(`${m.name} removed${data.unassigned ? ` · ${data.unassigned} booking(s) now unassigned` : ""}`); } catch (err) { toast.error(formatApiError(err)); }
  };
  const days = DAYS.filter(([d]) => m.hours?.[d]?.on).map(([d, l]) => `${l} ${fmt12(m.hours[d].start)}–${fmt12(m.hours[d].end)}`);
  return (
    <div className={`ad-card st-card ${m.active ? "" : "off"}`} data-testid={`team-card-${m.id}`}>
      <div className="st-top">
        <div><strong><span className="staff-dot" style={{ background: m.color, display: "inline-block", marginRight: 8 }} />{m.name}</strong><small>{m.title}</small></div>
        {!m.active && <span className="pill">Not taking bookings</span>}
      </div>
      <small className="muted-sm" data-testid="team-card-specialties">{m.categories.length + m.services.length ? `${m.categories.length} categor${m.categories.length === 1 ? "y" : "ies"} · ${m.services.length} individual service${m.services.length === 1 ? "" : "s"}` : "No services picked yet — won't appear in booking"}</small>
      <small className="muted-sm" data-testid="team-card-hours">{days.join(" · ") || "No working days"}</small>
      {m.days_off?.length > 0 && <small className="muted-sm">{m.days_off.length} day(s) off scheduled</small>}
      <small className="muted-sm" data-testid="team-card-login"><UserRound size={12} /> {m.login ? `${m.login.email} · last login ${m.login.last_login_at ? fmtWhen(m.login.last_login_at) : "never"}` : "No login"}</small>
      <div className="req-actions">
        <button className="btn light" onClick={onEdit} data-testid="team-edit-button">Edit</button>
        <button className="btn light" onClick={remove} aria-label={`Remove ${m.name}`} data-testid="team-delete-button"><Trash2 size={14} /></button>
      </div>
    </div>
  );
};

export default function TeamAdmin() {
  const { data } = useAdmin("team", "/admin/team");
  const [edit, setEdit] = useState(null);
  return (
    <div data-testid="admin-team">
      <PageHead eyebrow="Team" title="Team & schedules">
        <button className="btn gold" onClick={() => setEdit({})} data-testid="team-new-button"><Plus size={15} /> Add team member</button>
      </PageHead>
      <p className="muted-sm">Clients only see team members who do the chosen service, are working, and are free at that time. While no one is added here, booking works spa-wide like before.</p>
      {!data ? <Loading /> : data.length === 0 ? <Empty>No team members yet.</Empty> : <div className="st-list">{data.map((m) => <Card key={m.id} m={m} onEdit={() => setEdit(m)} />)}</div>}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="ad-msg-dialog tm-dialog" aria-describedby={undefined} data-testid="team-dialog">{edit && <Editor key={edit.id || "new"} m={edit} onDone={() => setEdit(null)} />}</DialogContent>
      </Dialog>
    </div>
  );
}
